/**
 * Format time eg: 1h 30m 6s (shows hours if >= 60 minutes, always shows minutes and seconds, no rounding)
 * @param {number} time in seconds
 * @returns Formatted time string
 */
export const getDuration = (time: number | string | undefined): string => {
  if (!time) return "";

  const timeNum = typeof time === 'string' ? parseInt(time) : time;
  if (isNaN(timeNum) || timeNum < 0) return "";

  // Calculate hours, minutes, and seconds
  const totalSeconds = Math.floor(timeNum);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (hours > 0 || minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }
  
  return parts.join(' ');
};

// Parses a time string ("4m 39s", "2m", "30s") or number into seconds.
function convertToSeconds(timeStr: string | number | undefined): number | null {
  if (!timeStr) return null;

  if (typeof timeStr === 'number') return timeStr;

  const timeString = String(timeStr).trim();

  const fullMatch = timeString.match(/(\d+)\s*(m|min|minute)\s*(\d+)\s*(s|sec|second)/i);
  if (fullMatch) {
    const minutes = parseInt(fullMatch[1]);
    const seconds = parseInt(fullMatch[3]);
    return minutes * 60 + seconds;
  }

  const minutesMatch = timeString.match(/(\d+)\s*(m|min|minute)/i);
  if (minutesMatch) {
    return parseInt(minutesMatch[1]) * 60;
  }

  const secondsMatch = timeString.match(/(\d+)\s*(s|sec|second)/i);
  if (secondsMatch) {
    return parseInt(secondsMatch[1]);
  }

  const numMatch = timeString.match(/(\d+)/);
  if (numMatch) {
    return parseInt(numMatch[1]);
  }
  
  return null;
}

/**
 * Get total duration of each course having lessons
 * @param {any} course Course object
 * @returns Formatted duration string
 */
export const getTotalCourseDuration = (course: any): string => {
  let totalDuration = 0;
  
  if (course?.is_standalone) {
    // Prioritize video_lesson duration, fallback to time_to_read
    let time = course?.video_lesson?.[0]?.duration
      ? course.video_lesson[0].duration
      : course?.lesson_details?.video_lesson?.[0]?.duration
      ? course.lesson_details.video_lesson[0].duration
      : course?.time_to_read
        ? convertToSeconds(course.time_to_read)
        : null;
    if (time) totalDuration = totalDuration + time;
  } else {
    course?.chapters?.[0]?.lessons?.lessons?.forEach((item: any) => {
      let time = item?.video_lesson?.[0]?.duration
        ? item.video_lesson[0].duration
        : item?.time_to_read 
          ? convertToSeconds(item.time_to_read) 
          : 0;
      if (time) totalDuration = totalDuration + time;
    });
  }

  if (totalDuration) {
    return getDuration(totalDuration);
  } else {
    return "";
  }
};

/** Same aggregation as `getTotalCourseDuration`, but returns seconds (integer) for MD export. */
export function getTotalCourseDurationSeconds(course: {
  is_standalone?: boolean;
  chapters?: { lessons?: { lessons?: Record<string, unknown>[] } }[];
  video_lesson?: { duration?: number }[];
  lesson_details?: { video_lesson?: { duration?: number }[] };
  time_to_read?: string;
}): number {
  let totalDuration = 0;

  if (course?.is_standalone) {
    const time =
      course?.video_lesson?.[0]?.duration ??
      course?.lesson_details?.video_lesson?.[0]?.duration ??
      (course?.time_to_read ? convertToSeconds(course.time_to_read) : null);
    if (time) totalDuration += time;
  } else {
    const chapters = course?.chapters ?? [];
    for (const ch of chapters) {
      const lessons = ch?.lessons?.lessons ?? [];
      for (const item of lessons) {
        const row = item as {
          video_lesson?: { duration?: number }[];
          time_to_read?: string;
        };
        const time =
          row?.video_lesson?.[0]?.duration ??
          (row?.time_to_read ? convertToSeconds(row.time_to_read) ?? 0 : 0);
        if (time) totalDuration += time;
      }
    }
  }

  return Math.max(0, Math.floor(totalDuration));
}
